import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const fieldTypes = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email Input' },
  { value: 'phone', label: 'Phone Input' },
  { value: 'number', label: 'Number Input' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'textarea', label: 'Text Area' }
];

export default function AdminContactFormEditor() {
  const [fields, setFields] = useState([]);
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    phone_numbers: [],
    email_addresses: [],
    office_locations: [],
    form_locations: [],
    workspace_types: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'text',
    placeholder: '',
    required: true,
    options: [],
    order: 0
  });
  const [newOption, setNewOption] = useState('');
  const [newItem, setNewItem] = useState('');

  const fetchFields = async () => {
    try {
      const response = await apiClient.get('/contact-form-fields');
      setFields(response.data);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await apiClient.get('/contact-section-content');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  useEffect(() => {
    fetchFields();
    fetchContent();
  }, []);

  const handleSaveContent = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put('/admin/contact-section-content', content);
      toast.success('Contact section updated!');
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleAddItem = (field) => {
    if (newItem.trim()) {
      setContent({ ...content, [field]: [...content[field], newItem.trim()] });
      setNewItem('');
    }
  };

  const handleRemoveItem = (field, index) => {
    setContent({ ...content, [field]: content[field].filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, id: editingField?.id || undefined };

      if (editingField) {
        await apiClient.put(`/admin/contact-form-fields/${editingField.id}`, payload);
        toast.success('Field updated!');
      } else {
        await apiClient.post('/admin/contact-form-fields', payload);
        toast.success('Field created!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchFields();
    } catch (error) {
      toast.error('Failed to save field');
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      label: field.label,
      type: field.type,
      placeholder: field.placeholder,
      required: field.required,
      options: field.options || [],
      order: field.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this field?')) {
      try {
        await apiClient.delete(`/admin/contact-form-fields/${id}`);
        toast.success('Field deleted!');
        fetchFields();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData({ ...formData, options: [...formData.options, newOption.trim()] });
      setNewOption('');
    }
  };

  const handleRemoveOption = (index) => {
    setFormData({ ...formData, options: formData.options.filter((_, i) => i !== index) });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      type: 'text',
      placeholder: '',
      required: true,
      options: [],
      order: 0
    });
    setEditingField(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contact Form Editor</h1>
            <p className="text-gray-600 mt-1">Manage contact section content and form fields</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="content" className="space-y-6">
          <TabsList>
            <TabsTrigger value="content">Contact Info & Text</TabsTrigger>
            <TabsTrigger value="dropdowns">Dropdown Options</TabsTrigger>
            <TabsTrigger value="fields">Custom Fields</TabsTrigger>
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content">
            <form onSubmit={handleSaveContent} className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Section Heading</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Title</Label>
                    <Input value={content.title} onChange={(e) => setContent({ ...content, title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea value={content.subtitle} onChange={(e) => setContent({ ...content, subtitle: e.target.value })} rows={2} />
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Phone Numbers</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="+91 98765 43210" />
                  <Button type="button" onClick={() => handleAddItem('phone_numbers')}>Add</Button>
                </div>
                <div className="space-y-1">
                  {content.phone_numbers.map((phone, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span>{phone}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveItem('phone_numbers', index)}><X size={16} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Email Addresses</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="info@thecircuit.work" />
                  <Button type="button" onClick={() => handleAddItem('email_addresses')}>Add</Button>
                </div>
                <div className="space-y-1">
                  {content.email_addresses.map((email, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span>{email}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveItem('email_addresses', index)}><X size={16} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">Office Locations</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Sarjapur Road, Bangalore - 560102" />
                  <Button type="button" onClick={() => handleAddItem('office_locations')}>Add</Button>
                </div>
                <div className="space-y-1">
                  {content.office_locations.map((location, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span>{location}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveItem('office_locations', index)}><X size={16} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={18} className="mr-2" />Save Content</Button>
              </div>
            </form>
          </TabsContent>

          {/* Dropdowns Tab */}
          <TabsContent value="dropdowns">
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <form onSubmit={handleSaveContent}>
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-3">Location Dropdown Options</h3>
                  <div className="flex gap-2 mb-2">
                    <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Sarjapur" />
                    <Button type="button" onClick={() => handleAddItem('form_locations')}>Add</Button>
                  </div>
                  <div className="space-y-1">
                    {content.form_locations.map((location, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                        <span>{location}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveItem('form_locations', index)}><X size={16} /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Workspace Type Dropdown Options</h3>
                  <div className="flex gap-2 mb-2">
                    <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Coworking Space" />
                    <Button type="button" onClick={() => handleAddItem('workspace_types')}>Add</Button>
                  </div>
                  <div className="space-y-1">
                    {content.workspace_types.map((type, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                        <span>{type}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveItem('workspace_types', index)}><X size={16} /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={18} className="mr-2" />Save Dropdown Options</Button>
                </div>
              </form>
            </div>
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="fields">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Add custom fields to extend the contact form. Default fields (name, email, phone) are built-in.
              </p>
            </div>

            <div className="flex justify-end mb-4">
              <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                <Plus size={18} className="mr-2" />Add Custom Field
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {fields.map((field) => (
            <div key={field.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{field.label}</h3>
                  <span className="text-xs text-gray-500">{field.name}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(field)}>
                    <Edit size={16} />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(field.id)}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Type:</strong> {field.type}</p>
                <p><strong>Required:</strong> {field.required ? 'Yes' : 'No'}</p>
                {field.placeholder && <p><strong>Placeholder:</strong> {field.placeholder}</p>}
                <p><strong>Order:</strong> {field.order}</p>
              </div>
            </div>
              ))}
            </div>

            {fields.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-600">No custom fields yet. Add one above!</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Field' : 'Add New Field'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Field Name (technical)</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="company_name"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Lowercase, no spaces (use underscores)</p>
            </div>
            <div>
              <Label>Field Label (displayed)</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Company Name"
                required
              />
            </div>
            <div>
              <Label>Field Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fieldTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Placeholder</Label>
              <Input
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                placeholder="Enter company name"
              />
            </div>
            {formData.type === 'select' && (
              <div>
                <Label>Options (for dropdown)</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add option"
                  />
                  <Button type="button" onClick={handleAddOption}>Add</Button>
                </div>
                <div className="space-y-1">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span>{option}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveOption(index)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                className="rounded"
              />
              <Label>Required field</Label>
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Field
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}